import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Plus, ShieldCheck, PencilSimple } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { listRules, createRule, updateRule } from '../../services/cmcApi';
import { CmcPageHeader, CmcStatusBadge, CmcEmpty, fmtDate } from '../../components/cmc/CmcCommon';

const FAMILIES = [
  'GOUVERNANCE', 'DEVELOPPEMENT', 'EXPERTISE',
  'PREVENTION_SANTE_NON_ASSURANTIELLE', 'PREVENTION_MOBILITE_VIEILLESSE',
  'ACCOMPAGNEMENT_VIE', 'SOUTIEN_FAMILIAL', 'SOUTIEN_SOCIAL',
  'SOUTIEN_EXCEPTIONNEL', 'FRAIS_CONTRIBUTEUR',
  'FORMATION_PARCOURS_COOPERATIF', 'ENVIRONNEMENT_TRANSITION', 'AUTRE',
];

const DIRECTIONS = [
  { value: 'inbound_contribution', label: 'Contribution entrante' },
  { value: 'outbound_support', label: 'Soutien sortant' },
  { value: 'outbound_reimbursement', label: 'Remboursement sortant' },
  { value: 'outbound_participation', label: 'Participation sortante' },
  { value: 'internal_affectation', label: 'Affectation interne' },
];

const BASES = [
  { value: 'verified_expense', label: 'Dépense vérifiée' },
  { value: 'committee_decision', label: 'Décision comité' },
  { value: 'ucc_score', label: 'Score UCC' },
  { value: 'distributable_surplus', label: 'Excédent distribuable' },
  { value: 'fixed_basis', label: 'Base fixe' },
  { value: 'manual_basis', label: 'Base manuelle' },
];

const RATE_TYPES = [
  { value: 'fixed', label: 'Fixe' },
  { value: 'percent', label: 'Pourcentage' },
  { value: 'manual', label: 'Manuel' },
  { value: 'bracket', label: 'Tranches' },
];

const CAP_PERIODS = [
  { value: 'none', label: 'Aucun' },
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'annual', label: 'Annuel' },
  { value: 'per_decision', label: 'Par décision' },
];

const VISIBILITY = [
  { value: 'minimal', label: 'Minimale' },
  { value: 'masked', label: 'Masquée' },
  { value: 'internal_only', label: 'Interne uniquement' },
];

const emptyForm = {
  code: '',
  label: '',
  family: 'GOUVERNANCE',
  description: '',
  direction: 'outbound_support',
  basis: 'committee_decision',
  rate_type: 'manual',
  rate: '',
  min_amount: '',
  cap_amount: '',
  cap_period: 'per_decision',
  non_insurance: true,
  non_automatic: true,
  qr_public_visibility: 'minimal',
  active: true,
};

export default function CmcRulesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterActive, setFilterActive] = useState('all');
  const [filterFamily, setFilterFamily] = useState('all');
  const [filterDirection, setFilterDirection] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterActive !== 'all') params.active = filterActive === 'true';
      if (filterFamily !== 'all') params.family = filterFamily;
      if (filterDirection !== 'all') params.direction = filterDirection;
      const res = await listRules(params);
      setItems(res.data.items || []);
    } catch {
      toast.error('Erreur de chargement des règles CMC');
    } finally {
      setLoading(false);
    }
  }, [filterActive, filterFamily, filterDirection]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      code: r.code || '',
      label: r.label || '',
      family: r.family || 'GOUVERNANCE',
      description: r.description || '',
      direction: r.direction || 'outbound_support',
      basis: r.basis || 'committee_decision',
      rate_type: r.rate_type || 'manual',
      rate: r.rate ?? '',
      min_amount: r.min_amount ?? '',
      cap_amount: r.cap_amount ?? '',
      cap_period: r.cap_period || 'per_decision',
      non_insurance: r.non_insurance !== false,
      non_automatic: r.non_automatic !== false,
      qr_public_visibility: r.qr_public_visibility || 'minimal',
      active: r.active !== false,
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.code || !form.label) {
      toast.error('Code et libellé requis');
      return;
    }
    const payload = {
      ...form,
      rate: form.rate === '' || form.rate === null ? null : parseFloat(form.rate),
      min_amount: form.min_amount === '' || form.min_amount === null ? null : parseFloat(form.min_amount),
      cap_amount: form.cap_amount === '' || form.cap_amount === null ? null : parseFloat(form.cap_amount),
    };
    setSaving(true);
    try {
      if (editing) {
        // On edit, the code is immutable on the server but we keep label etc.
        const { code, ...updatePayload } = payload;
        await updateRule(editing.id, updatePayload);
        toast.success('Règle mise à jour');
      } else {
        await createRule(payload);
        toast.success('Règle créée');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : detail?.[0]?.msg || 'Erreur lors de la sauvegarde'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="cmc-rules-page">
      <CmcPageHeader
        title="Règles CMC"
        subtitle="Référentiel des règles de qualification, validation et plafonds CMC"
        actions={
          <Button
            onClick={openCreate}
            className="bg-[#2D5A4C] hover:bg-[#1E4136] text-white"
            data-testid="rule-create-btn"
          >
            <Plus size={16} className="mr-2" /> Nouvelle règle
          </Button>
        }
      />

      <Card className="border-[#E8E6E1] mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <Select value={filterActive} onValueChange={setFilterActive}>
            <SelectTrigger className="w-[160px]" data-testid="rule-filter-active"><SelectValue placeholder="Activation" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="true">Actives</SelectItem>
              <SelectItem value="false">Inactives</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterFamily} onValueChange={setFilterFamily}>
            <SelectTrigger className="w-[220px]" data-testid="rule-filter-family"><SelectValue placeholder="Famille" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes familles</SelectItem>
              {FAMILIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterDirection} onValueChange={setFilterDirection}>
            <SelectTrigger className="w-[220px]" data-testid="rule-filter-direction"><SelectValue placeholder="Direction" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes directions</SelectItem>
              {DIRECTIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
          ) : items.length === 0 ? (
            <CmcEmpty icon={ShieldCheck} message="Aucune règle CMC" hint="Créez une règle pour démarrer" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F8F5] text-[11px] uppercase tracking-wide text-[#5E6D66]">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Code</th>
                    <th className="text-left px-4 py-2.5 font-medium">Libellé</th>
                    <th className="text-left px-4 py-2.5 font-medium">Famille</th>
                    <th className="text-left px-4 py-2.5 font-medium">Direction</th>
                    <th className="text-left px-4 py-2.5 font-medium">Taux</th>
                    <th className="text-left px-4 py-2.5 font-medium">Plafond</th>
                    <th className="text-left px-4 py-2.5 font-medium">État</th>
                    <th className="text-left px-4 py-2.5 font-medium">MAJ</th>
                    <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0EFEB]">
                  {items.map((r) => (
                    <tr key={r.id} className="hover:bg-[#FAFAF8]" data-testid={`rule-row-${r.id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-[#1A2421]">{r.code}</td>
                      <td className="px-4 py-3 text-[#1A2421] font-medium">{r.label}</td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{r.family}</td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">
                        {DIRECTIONS.find((d) => d.value === r.direction)?.label || r.direction}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {r.rate != null ? `${r.rate}${r.rate_type === 'percent' ? '%' : ''}` : <span className="text-[#5E6D66]">{RATE_TYPES.find((t) => t.value === r.rate_type)?.label || '-'}</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">
                        {r.cap_amount != null ? `${r.cap_amount} € / ${CAP_PERIODS.find((p) => p.value === r.cap_period)?.label || r.cap_period}` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <CmcStatusBadge
                          status={r.active ? 'active' : 'draft'}
                          label={r.active ? 'Active' : 'Inactive'}
                        />
                        {r.is_system && (
                          <div className="text-[10px] text-[#5E6D66] mt-0.5">Système</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{fmtDate(r.updated_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`rule-edit-${r.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier la règle CMC' : 'Nouvelle règle CMC'}</DialogTitle>
            <DialogDescription>
              Définissez la qualification, la base de calcul, les plafonds et les approbations requises.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div>
              <Label>Code *</Label>
              <Input
                data-testid="rule-form-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={!!editing}
                placeholder="ex: RULE_SOUTIEN_SOCIAL"
              />
            </div>
            <div>
              <Label>Libellé *</Label>
              <Input data-testid="rule-form-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div>
              <Label>Famille</Label>
              <Select value={form.family} onValueChange={(v) => setForm({ ...form, family: v })}>
                <SelectTrigger data-testid="rule-form-family"><SelectValue /></SelectTrigger>
                <SelectContent>{FAMILIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger data-testid="rule-form-direction"><SelectValue /></SelectTrigger>
                <SelectContent>{DIRECTIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea data-testid="rule-form-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Base de calcul</Label>
              <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                <SelectTrigger data-testid="rule-form-basis"><SelectValue /></SelectTrigger>
                <SelectContent>{BASES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type de taux</Label>
              <Select value={form.rate_type} onValueChange={(v) => setForm({ ...form, rate_type: v })}>
                <SelectTrigger data-testid="rule-form-rate-type"><SelectValue /></SelectTrigger>
                <SelectContent>{RATE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Taux ({form.rate_type === 'percent' ? '%' : '€'})</Label>
              <Input type="number" step="0.01" data-testid="rule-form-rate" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
            </div>
            <div>
              <Label>Montant min (€)</Label>
              <Input type="number" step="0.01" data-testid="rule-form-min" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} />
            </div>
            <div>
              <Label>Plafond (€)</Label>
              <Input type="number" step="0.01" data-testid="rule-form-cap" value={form.cap_amount} onChange={(e) => setForm({ ...form, cap_amount: e.target.value })} />
            </div>
            <div>
              <Label>Période plafond</Label>
              <Select value={form.cap_period} onValueChange={(v) => setForm({ ...form, cap_period: v })}>
                <SelectTrigger data-testid="rule-form-cap-period"><SelectValue /></SelectTrigger>
                <SelectContent>{CAP_PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visibilité QR publique</Label>
              <Select value={form.qr_public_visibility} onValueChange={(v) => setForm({ ...form, qr_public_visibility: v })}>
                <SelectTrigger data-testid="rule-form-qr-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>{VISIBILITY.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 grid grid-cols-3 gap-4 pt-2">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="rule-form-active-wrap">
                <Switch checked={form.active} onCheckedChange={(c) => setForm({ ...form, active: c })} data-testid="rule-form-active" />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={form.non_insurance} onCheckedChange={(c) => setForm({ ...form, non_insurance: c })} data-testid="rule-form-non-insurance" />
                <span className="text-sm">Non assurantiel</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={form.non_automatic} onCheckedChange={(c) => setForm({ ...form, non_automatic: c })} data-testid="rule-form-non-automatic" />
                <span className="text-sm">Non automatique</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="rule-cancel-btn">Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="rule-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Mettre à jour' : 'Créer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
